import { useTranslation } from "next-i18next";
import { useState } from "react";

import { AddressData } from "@/api/address/types";
import { AddressPicker } from "@/components/AddressPicker";
import AddressPickerFallback from "@/components/AddressPickerFallback";
import { CustomerInfoDisplay } from "@/components/CustomerInfoDisplay";
import { Button, Form } from "@/components/elements";
import { useUpdateAddress } from "@/hooks/address";
import { useProfile } from "@/hooks/profile";
import { useFlag } from "@/hooks/useFlag/useFlag";
import useFormatAddress from "@/hooks/useFormatAddress";

export type CustomerAddressProps = {
  editCta?: string;
  setIsEditing?: (isEditing: boolean) => void;
  isEditing?: boolean;
  hideName?: boolean;
  className?: string;
};

export const CustomerAddress = ({
  editCta = "personal_information_edit_account",
  setIsEditing,
  isEditing,
  hideName,
  className,
}: CustomerAddressProps) => {
  const { t } = useTranslation();
  const { data } = useProfile();
  const { first_name, last_name, address: addressForFormatting } = data || {};
  const { mutate, isLoading, error } = useUpdateAddress();
  const showManualAddress = useFlag("manual-address");

  const addressFormatted = useFormatAddress(
    `${first_name} ${last_name}`,
    addressForFormatting,
    hideName
  );

  const [address, setAddress] = useState<AddressData>();

  return (
    <section className={className}>
      {isEditing ? (
        <Form
          legend={t("delivery_information_title")}
          hideLegend
          primaryButtonText={t("save")}
          primaryButtonTheme="outline"
          secondaryButtonText={t("cancel_CTA")}
          secondaryButtonAction={() => setIsEditing(false)}
          serverError={error}
          isLoading={isLoading}
          onSubmit={(e) => {
            e.preventDefault();

            mutate(address, {
              onSuccess: () => setIsEditing(false),
            });
          }}
          disabled={isLoading || !address}
        >
          {showManualAddress ? (
            <AddressPickerFallback onChange={setAddress} />
          ) : (
            <AddressPicker onUpdate={setAddress} />
          )}
        </Form>
      ) : (
        <>
          <CustomerInfoDisplay
            label={
              hideName
                ? t("account_address")
                : t("personal_information_name_address")
            }
            value={addressFormatted}
          />
          <div>
            {setIsEditing ? (
              <Button
                onClick={() => {
                  setIsEditing(true);
                }}
                theme="underline"
                size="small"
              >
                {t(editCta)}
              </Button>
            ) : (
              <Button pageLink="ACCOUNT_EDIT" theme="underline" size="small">
                {t(editCta)}
              </Button>
            )}
          </div>
        </>
      )}
    </section>
  );
};
